// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/* eslint-disable no-control-regex */
/* eslint-disable security/detect-unsafe-regex */
/* eslint-disable security/detect-object-injection */

import { IMessageSink } from '../utilities';

/** Learning model value constraints. */
export const constraints = {
    id: stringConstraint(
        500,
        /^[a-z\d.](?:[a-z\d.-]{0,48}[a-z\d.])?(?::[a-z\d.](?:[a-z\d.-]{0,48}[a-z\d.])?){0,9}$/,
        'Value must be a string between 1 and 500 characters long, containing only lowercase letters, digits, dots, and hyphens.'
    ),
    duration: numberConstraint(0, 500000, 'Value must be a number between 0 and 500000 inclusive.'),
    title: stringConstraint(
        500,
        /^(?:\S|\S.{0,498}\S)$/,
        'Value must be a string between 1 and 500 characters long, not starting or ending with whitespace.'
    ),
    description: stringConstraint(
        4000,
        /^(?:\S|\S.{0,3998}\S)$/m,
        'Value must be a string between 1 and 4000 characters long, not starting or ending with whitespace.'
    ),
    difficultyLevel: stringConstraint(
        30,
        /^beginner|intermediate|advanced|expert$/,
        "Value must be one of 'beginner', 'intermediate', 'advanced', or 'expert'."
    ),
    displayName: stringConstraint(
        100,
        /^(?:\S|\S.{0,98}\S)$/,
        'Value must be a string between 1 and 100 characters long, not starting or ending with whitespace.'
    ),
    email: stringConstraint(
        100,
        /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]{1,50}(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]{1,50}){0,50}|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f]){0,50}")@(?:(?:[a-z0-9](?:[a-z0-9-]{0,50}[a-z0-9])?\.){1,50}[a-z0-9](?:[a-z0-9-]{0,50}[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]{0,50}[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f]){1,50})\])$/,
        'Value must be a string between 1 and 100 characters long, containing a valid email address.'
    ),
    url: stringConstraint(
        500,
        /^(?:(https?:\/\/(?:www\.)?[-\w@:%.+~#=]{1,256}\.[a-zA-Z\d()]{1,6}\b[-\w()@:%+.~#?&/=]{0,740}))$/,
        'Value must be a string between 1 and 500 characters long, containing a valid URL.'
    ),
    path: stringConstraint(
        500,
        /^(?:[a-zA-Z]:[\\/]|\\\\|\/)?(?:[^<>:"/\\|?*]+(?:[\\/](?![\\/]))?)*[^<>:"/\\|?*]*$/,
        'Value must be a string between 1 and 500 characters long, containing a valid file path.'
    ),
    enum: enumConstraint(),
};

/** Defines a string property constraint. */
function stringConstraint(maxLength: number, pattern: RegExp, message: string) {
    return {
        maxLength,
        pattern,
        message,
        validate: <T extends object>(object: T, name: keyof T, messages: IMessageSink) => {
            const value = object[name] as string;
            if (value !== undefined && (value.length > maxLength || !pattern.test(value))) {
                messages.error(`${object}: ${name as string}: ${message}`);
            }
        },
    };
}

/** Defines a number property constraint. */
function numberConstraint(minimum: number, maximum: number, message: string) {
    return {
        minimum,
        maximum,
        message,
        validate: <T extends object>(object: T, name: keyof T, messages: IMessageSink) => {
            const value = object[name] as number;
            if (value !== undefined && (value < minimum || value > maximum)) {
                messages.error(`${object}: ${name as string}: ${message}`);
            }
        },
    };
}

function enumConstraint() {
    return {
        validate: <T extends object>(
            object: T,
            name: keyof T,
            enumValidator: (value: string | undefined, name: string) => string | undefined,
            messages: IMessageSink
        ) => {
            const value = object[name] as string;
            const message = enumValidator(value, name as string);
            if (message !== undefined) {
                messages.error(`${object}: ${name as string}: ${message}`);
            }
        },
    };
}
